import React from "react";
import { Link } from "react-router-dom";
import { isCollectorEnabled } from "../../../helpers/collectorSession";
import { isStudioLoggedIn } from "../../../helpers/studioSignupSession";
import SiteLayout from "./SiteLayout";
import { Ripple } from "./SiteBits";
import loomImg from "../assets/images/site/front-page-1-3.jpg";
import brocadeImg from "../assets/images/site/buyer-trust.jpg";

type Module = [glyph: string, name: string, body: string, plan: "core" | "trial"];

const MODULES: Module[] = [
  ["◎", "Product records", "Identity, process media, materials and the maker's story live with the piece — not in a brochure.", "core"],
  ["✓", "Certificates & tags", "Issue a certificate buyers can check from any phone. Bind an NFC tag or pre-printed QR label.", "core"],
  ["⌗", "Catalogue", "Share with password, view limits and expiry — open, exclusive or reseller per link.", "trial"],
  ["⫴", "Karkhana", "Looms, daily work, attendance, rate cards, helpers, advances and wages for the floor.", "trial"],
  ["⇄", "Orders & capture", "Studio orders, and field capture links so process media arrives without a full login.", "trial"],
  ["⌖", "Geo, GI & looms", "GI fences, loom registry and organisation certifications.", "trial"],
  ["₹", "Saree costing", "Per-piece costing with an approval step that flows into product prices.", "trial"],
  ["✦", "AI enrichment", "Vision-assisted descriptions, craft tags and stories drafted from your photos.", "trial"],
];

const STEPS: [string, string][] = [
  ["Create your organisation", "Name, your login and a password. GST and address can wait."],
  ["Add a product", "Record the piece, process and story you want on the certificate."],
  ["Certify, tag, share", "Issue the certificate, attach a tag when ready, and share a catalogue."],
];

const StudioPage = () => {
  const signedIn = isStudioLoggedIn();
  const primaryTo = signedIn ? "/dashboard" : "/studio/signup";
  const primaryLabel = signedIn ? "Open Studio" : "Start free studio — 7 days, every module";

  return (
    <SiteLayout title="Studio for makers — HastKala Pramanak">
      <section className="c-hero">
        <div className="s-wrap c-hero__grid">
          <div>
            <span className="s-eyebrow">Studio · for makers who want traceability</span>
            <h1>
              Your craft,
              <br />
              <span className="s-grad">on the record.</span>
            </h1>
            <p className="s-lead">
              For weavers, master weavers, designers, manufacturers and organisations who need a record they can
              prove. Record each piece, certify it, tag it, and share a catalogue you control.
            </p>
            <div className="s-ctas">
              <Link className="s-btn s-btn--primary" to={primaryTo}>{primaryLabel}</Link>
              {!signedIn && <Link className="s-btn s-btn--ghost" to="/login">Studio log in</Link>}
            </div>
            <div className="c-pricebar">
              <div><b>7 days</b><span>every module free</span></div>
              <div><b>Core</b><span>always included</span></div>
              <div><b>No card</b><span>to start the trial</span></div>
            </div>
          </div>
          <div className="c-photo">
            <img src={loomImg} alt="A weaver's hands working at a handloom" style={{ objectPosition: "30% center" }} />
            <div className="c-tap" aria-hidden="true">
              <Ripple />
              <small>CERTIFICATE · ISSUED</small>
              <p>Kadhua Banarasi</p>
            </div>
            <div className="c-phone" aria-hidden="true">
              <div className="c-phone__scr">
                <div className="c-phone__top">Studio<span>today</span></div>
                <div className="c-item"><img src={brocadeImg} alt="" /><div><b>Kadhua Banarasi</b><span>6 / 6 stages</span><br /><span className="c-tagl">Certified · NFC</span></div></div>
                <div className="c-lock"><i>●</i>Loom 7 · 2 weavers on shift</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="s-section s-reveal">
        <div className="s-wrap">
          <div className="s-head">
            <div>
              <span className="s-eyebrow">What you get</span>
              <h2>Every module free for 7 days. Keep only what you need.</h2>
            </div>
            <p className="s-lead">
              Studio core — product records, certificates and tags — is always on. The rest you try first, then keep
              the ones your studio uses.
            </p>
          </div>
          <div className="c-feat">
            {MODULES.map(([glyph, name, body, plan]) => (
              <div className="c-f" key={name}>
                <span className="c-f__ic" aria-hidden="true">{glyph}</span>
                <h3>{name}</h3>
                <p>{body}</p>
                <span className={`s-pill ${plan === "core" ? "s-pill--ok" : "s-pill--blue"}`} style={{ display: "inline-block", marginTop: "var(--s2)" }}>
                  {plan === "core" ? "Always included" : "Free in trial · then per module"}
                </span>
              </div>
            ))}
            <div className="c-f" style={{ background: "var(--night)", borderColor: "var(--night)", color: "#fff" }}>
              <span className="c-f__ic" aria-hidden="true" style={{ background: "rgba(90,200,250,.12)", color: "var(--signal)" }}>⌘</span>
              <h3>HKP inside Claude</h3>
              <p style={{ color: "var(--on-night-2)" }}>
                Run orders, stock and catalogues from a chat with our MCP connector — every change waits for your
                confirm.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="s-section s-section--tight s-reveal">
        <div className="s-wrap">
          <div className="s-head"><div><span className="s-eyebrow">How studios start</span><h2>Your first certificate in one sitting.</h2></div></div>
          <div className="c-steps">
            {STEPS.map(([title, body], i) => (
              <div className="c-step" key={title}>
                <span className="s-mono">{String(i + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="s-final s-reveal">
        <div className="s-wrap">
          <span className="s-eyebrow">Start today</span>
          <h2>Give your craft the proof it deserves.</h2>
          <p className="s-lead">
            Bought a piece and want to keep its story?{" "}
            {isCollectorEnabled() ? <Link to="/collectors" style={{ color: "var(--blue)" }}>My Collection</Link> : "My Collection"} is for you.
          </p>
          <div className="s-ctas">
            <Link className="s-btn s-btn--primary" to={primaryTo}>{signedIn ? "Open Studio" : "Start free studio"}</Link>
            <Link className="s-btn s-btn--ghost" to="/pricing">See pricing</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default StudioPage;
